import {
  decrireVerification,
  echeancier as sch,
  formaterJour,
  formaterMontant,
  MOYENS_PAIEMENT,
  paiements as schP,
  verificationRegle,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { api, corps, ecrit, ErreurApi, gere, lire, type Regle, requeteMoi, requetes } from '../api'
import { SectionConsignes } from '../composants/Consignes'
import { DepenseIaDeMesEspaces } from '../composants/EspacesPrives'
import { type Champ, Formulaire } from '../composants/Formulaire'
import { formaterInstant } from '../composants/Historique'
import { Icone } from '../composants/Icone'
import { Logo } from '../composants/Logo'
import { Ligne, Section, Vide } from '../composants/Section'
import {
  LIBELLES_CERTITUDE,
  LIBELLES_ETAT_ACHEMINEMENT,
  LIBELLES_MOYEN,
  LIBELLES_TYPE_PIECE,
  LIBELLES_UNITE_PERIODE,
  nomMois,
  options,
} from '../libelles'
import { EntetePage, Onglets } from '../composants/Ui'
import { Banques } from './ReglagesBanques'
import { Fournisseurs } from './ReglagesFournisseurs'
import { Ordinateurs } from './ReglagesOrdinateurs'
import { Portails } from './ReglagesPortails'
import { SectionNotifications } from '../composants/Notifications'
import { confirmer } from '../composants/confirmation'

// Réglages, en onglets : échéances (règles, délais de grâce), consignes de tri, connexions
// (en sous-onglets : boîtes mail, banques, portails, ordinateurs), ce qui est à soi (digest,
// agenda) et, pour les administrateurs, le système.

export const ONGLETS_REGLAGES = ['echeances', 'tri', 'connexions', 'moi', 'systeme'] as const
export type OngletReglages = (typeof ONGLETS_REGLAGES)[number]
export const ONGLETS_CONNEXIONS = [
  'boites',
  'banques',
  'portails',
  'fournisseurs',
  'ordinateurs',
] as const
export type OngletConnexions = (typeof ONGLETS_CONNEXIONS)[number]

const JOURS_SEMAINE: Record<string, string> = {
  MO: 'lundi',
  TU: 'mardi',
  WE: 'mercredi',
  TH: 'jeudi',
  FR: 'vendredi',
  SA: 'samedi',
  SU: 'dimanche',
}

function decouper(rrule: string) {
  const depuis = /DTSTART[^:]*:(\d{4})(\d{2})(\d{2})/.exec(rrule)
  const regle = /RRULE:(.+)/.exec(rrule)
  return {
    depuis: depuis ? `${depuis[1]}-${depuis[2]}-${depuis[3]}` : '2026-01-01',
    recurrence: regle?.[1]?.trim() ?? '',
  }
}

/** Description française des récurrences courantes ; sinon la RRULE brute. */
function decrire(rrule: string): string {
  const { depuis, recurrence } = decouper(rrule)
  const p = Object.fromEntries(recurrence.split(';').map((x) => x.split('=') as [string, string]))
  const jour = p.BYMONTHDAY === '-1' ? 'le dernier jour' : p.BYMONTHDAY ? `le ${p.BYMONTHDAY}` : ''
  if (p.COUNT === '1') return `une fois, le ${formaterJour(depuis)}`
  if (p.FREQ === 'MONTHLY' && jour) return `chaque mois, ${jour}`
  if (p.FREQ === 'YEARLY' && p.BYMONTH && jour) {
    const mois = p.BYMONTH.split(',').map(
      (m) => nomMois(`2000-${m.padStart(2, '0')}`).split(' ')[0],
    )
    return `chaque année, ${jour} ${mois.join(', ')}`
  }
  if (p.FREQ === 'WEEKLY' && p.BYDAY) return `chaque ${JOURS_SEMAINE[p.BYDAY] ?? p.BYDAY}`
  return recurrence
}

const LIBELLES_SENS = { payer: 'À payer', encaisser: 'À encaisser' } as const

/** Ce qu'une vérification attend : un mouvement du relevé (sens) ou une pièce. */
const LIBELLES_VERIFICATION = {
  encaisser: 'Encaissement sur le relevé',
  payer: 'Débit sur le relevé',
  piece: 'Pièce reçue',
} as const

function champsRegle(
  creation: { entites: { id: string; nom: string }[] } | null,
  tiers: { id: string; nom: string }[],
  baux: { valeur: string; libelle: string }[],
): Champ[] {
  return [
    ...(creation
      ? ([
          {
            nom: 'entiteId',
            libelle: 'Entité',
            type: 'choix',
            requis: true,
            options: creation.entites.map((e) => ({ valeur: e.id, libelle: e.nom })),
          },
          {
            nom: 'type',
            libelle: 'Code',
            type: 'texte',
            requis: true,
            aide: 'Minuscules : tva_ca3, cfe…',
          },
        ] satisfies Champ[])
      : []),
    { nom: 'libelle', libelle: 'Libellé', type: 'texte', requis: true },
    { nom: 'depuis', libelle: 'À partir du', type: 'date', requis: true },
    {
      nom: 'recurrence',
      libelle: 'Récurrence (RRULE)',
      type: 'texte',
      requis: true,
      aide: 'FREQ=MONTHLY;BYMONTHDAY=15 · FREQ=YEARLY;BYMONTH=5;BYMONTHDAY=15 · FREQ=DAILY;COUNT=1',
    },
    {
      nom: 'periodeUnite',
      libelle: 'Période déclarée',
      type: 'choix',
      requis: true,
      options: options(LIBELLES_UNITE_PERIODE),
    },
    {
      nom: 'periodeDecalage',
      libelle: 'Décalage de période',
      type: 'entier',
      requis: true,
      aide: '-1 : mois, trimestre ou année écoulés ; 0 : en cours',
    },
    {
      nom: 'fenetreJours',
      libelle: 'Fenêtre (jours)',
      type: 'entier',
      aide: 'Du 15 au 24 : 9. Vide : date simple.',
    },
    { nom: 'reportJourOuvre', libelle: 'Reporter au jour ouvré suivant', type: 'booleen' },
    {
      nom: 'certitude',
      libelle: 'Certitude',
      type: 'choix',
      requis: true,
      options: options(LIBELLES_CERTITUDE),
    },
    { nom: 'montantPrevuCts', libelle: 'Montant prévu (€)', type: 'montant' },
    {
      nom: 'paiementSens',
      libelle: 'Paiement attendu',
      type: 'choix',
      options: options(LIBELLES_SENS),
      aide: 'Vide : échéances et tâches seulement. Le paiement est créé pour chaque échéance qui a un montant, puis rapproché du relevé.',
    },
    {
      nom: 'paiementMoyen',
      libelle: 'Moyen de paiement',
      type: 'choix',
      options: options(LIBELLES_MOYEN),
    },
    {
      nom: 'paiementTiersId',
      libelle: 'Tiers du paiement',
      type: 'choix',
      options: tiers.map((t) => ({ valeur: t.id, libelle: t.nom })),
      aide: 'Facultatif ; ses libellés bancaires aident le rapprochement.',
    },
    {
      nom: 'verificationQuoi',
      libelle: 'Vérifier à chaque période',
      type: 'choix',
      options: options(LIBELLES_VERIFICATION),
      aide: 'Vide : règle ordinaire. Sinon l’appli cherche seule ce qui doit arriver : trouvé, l’échéance est faite ; rien passé le délai (relevé à jour), une tâche aux responsables. Ni paiement attendu ni tâches.',
    },
    {
      nom: 'verificationTiersId',
      libelle: 'Tiers attendu',
      type: 'choix',
      options: tiers.map((t) => ({ valeur: t.id, libelle: t.nom })),
      aide: 'Relevé : ses libellés bancaires ou son IBAN ; pièce : son émetteur.',
    },
    {
      nom: 'verificationLibelle',
      libelle: 'Mots attendus',
      type: 'texte',
      aide: 'Dans le libellé du relevé (« SALESFORCE »), ou le nom, l’émetteur ou l’objet de la pièce. Le tiers ou ces mots, au moins.',
    },
    {
      nom: 'verificationTypePiece',
      libelle: 'Type de pièce',
      type: 'choix',
      options: options(LIBELLES_TYPE_PIECE),
    },
    { nom: 'verificationMontantMinCts', libelle: 'Montant minimum (€)', type: 'montant' },
    { nom: 'verificationMontantMaxCts', libelle: 'Montant maximum (€)', type: 'montant' },
    {
      nom: 'verificationDelaiJours',
      libelle: 'Délai avant de signaler (jours)',
      type: 'entier',
      aide: 'Après la date limite ; 5 par défaut.',
    },
    { nom: 'note', libelle: 'Note', type: 'texte' },
    {
      nom: 'bailId',
      libelle: 'Bail',
      type: 'choix',
      options: baux,
      aide: 'Loyer, attestation annuelle… Son gestionnaire peut exécuter les tâches.',
    },
    {
      nom: 'taches',
      libelle: 'Tâches de chaque échéance',
      type: 'liste',
      aide: 'Une par ligne : action | titre | jours ouvrés avant (-) ou après la date limite | exécutant (cabinet, assistant ou gestionnaire du bail, facultatif). « payer » porte le paiement.',
    },
    { nom: 'active', libelle: 'Active', type: 'booleen' },
  ]
}

type ModeleTache = {
  action: string
  titre: string
  decalageJoursOuvres: number
  executant?: string
}

function modelesVersLignes(modeles: readonly ModeleTache[]): string[] {
  return modeles.map((m) =>
    [
      m.action,
      m.titre,
      m.decalageJoursOuvres,
      m.executant && m.executant !== 'responsables' ? m.executant : null,
    ]
      .filter((x) => x !== null && x !== undefined)
      .join(' | '),
  )
}

/** « preparer | Préparer la TVA | -3 » ; une ligne mal formée est renvoyée telle quelle au schéma, qui la refuse. */
function lignesVersModeles(lignes: unknown): unknown[] {
  if (!Array.isArray(lignes)) return []
  return lignes.map((ligne: string) => {
    const [action, titre, decalage, executant] = ligne.split('|').map((x) => x.trim())
    return {
      action,
      titre,
      decalageJoursOuvres:
        decalage !== undefined && /^-?\d+$/.test(decalage) ? Number(decalage) : decalage,
      ...(executant && { executant }),
    }
  })
}

function versFormulaire(r: Regle) {
  const { depuis, recurrence } = decouper(r.rrule)
  return {
    ...r,
    depuis,
    recurrence,
    periodeUnite: r.decalage.periode.unite,
    periodeDecalage: r.decalage.periode.decalage,
    fenetreJours: r.decalage.fenetreJours ?? null,
    reportJourOuvre: r.decalage.reportJourOuvre ?? false,
    taches: modelesVersLignes(r.taches),
    paiementSens: r.paiement?.sens ?? null,
    paiementMoyen: r.paiement?.moyen ?? null,
    paiementTiersId: r.paiement?.tiersId ?? null,
    ...versFormulaireVerification(r.verification),
  }
}

function versFormulaireVerification(v: Regle['verification']) {
  return {
    verificationQuoi: v ? (v.source === 'piece' ? 'piece' : (v.sens ?? 'encaisser')) : null,
    verificationTiersId: v?.tiersId ?? null,
    verificationLibelle: v?.libelle ?? null,
    verificationTypePiece: v?.typePiece ?? null,
    verificationMontantMinCts: v?.montantMinCts ?? null,
    verificationMontantMaxCts: v?.montantMaxCts ?? null,
    verificationDelaiJours: v?.delaiJours ?? null,
  }
}

/** Champ du formulaire qui porte chaque clé de la vérification, pour y montrer l'erreur. */
const CHAMPS_VERIFICATION: Record<string, string> = {
  tiersId: 'verificationLibelle',
  libelle: 'verificationLibelle',
  typePiece: 'verificationTypePiece',
  montantMinCts: 'verificationMontantMinCts',
  montantMaxCts: 'verificationMontantMaxCts',
  delaiJours: 'verificationDelaiJours',
}

/**
 * Les champs de la vérification ; sans « Vérifier », pas de vérification. Le compte désigné
 * (posé par l'assistant) est gardé tel quel.
 */
function verificationDepuisFormulaire(v: Record<string, unknown>, compteId: string | null) {
  const quoi = v.verificationQuoi
  if (quoi === null) return null
  const piece = quoi === 'piece'
  const r = verificationRegle.safeParse({
    source: piece ? 'piece' : 'banque',
    ...(piece
      ? { typePiece: v.verificationTypePiece }
      : { sens: quoi, ...(compteId && { compteId }) }),
    tiersId: v.verificationTiersId,
    libelle: v.verificationLibelle,
    montantMinCts: v.verificationMontantMinCts,
    montantMaxCts: v.verificationMontantMaxCts,
    ...(v.verificationDelaiJours !== null && { delaiJours: v.verificationDelaiJours }),
  })
  if (r.success) return r.data
  throw new ErreurApi(
    'Saisie invalide.',
    400,
    r.error.issues.map((i) => ({
      champ: CHAMPS_VERIFICATION[String(i.path[0])] ?? 'verificationQuoi',
      message: i.message,
    })),
  )
}

/** Les trois champs du paiement attendu ; sans sens, pas de paiement. */
function paiementDepuisFormulaire(sens: unknown, moyen: unknown, tiersId: unknown) {
  if (sens === null) return null
  if (moyen === null)
    throw new ErreurApi('Saisie invalide.', 400, [
      { champ: 'paiementMoyen', message: 'Obligatoire avec un paiement attendu.' },
    ])
  return { sens, moyen, tiersId }
}

function depuisFormulaire(v: Record<string, unknown>, avant?: Regle) {
  const {
    verificationQuoi: _quoi,
    verificationTiersId: _tiers,
    verificationLibelle: _libelle,
    verificationTypePiece: _type,
    verificationMontantMinCts: _min,
    verificationMontantMaxCts: _max,
    verificationDelaiJours: _delai,
    depuis,
    recurrence,
    periodeUnite,
    periodeDecalage,
    fenetreJours,
    reportJourOuvre,
    taches,
    paiementSens,
    paiementMoyen,
    paiementTiersId,
    ...reste
  } = v
  return {
    ...reste,
    taches: lignesVersModeles(taches),
    paiement: paiementDepuisFormulaire(paiementSens, paiementMoyen, paiementTiersId),
    verification: verificationDepuisFormulaire(v, avant?.verification?.compteId ?? null),
    rrule: `DTSTART:${String(depuis).replaceAll('-', '')}T000000Z\nRRULE:${String(recurrence).replace(/^RRULE:/, '')}`,
    decalage: {
      periode: { unite: periodeUnite, decalage: periodeDecalage },
      ...(fenetreJours !== null && { fenetreJours }),
      reportJourOuvre,
    },
  }
}

function Bilan({
  bilan,
}: {
  bilan: { creees: number; deplacees: number; retirees: number } | null
}) {
  if (!bilan) return null
  return (
    <p className="discret">
      Échéancier mis à jour : {bilan.creees} créées, {bilan.deplacees} déplacées, {bilan.retirees}{' '}
      retirées.
    </p>
  )
}

function DelaisGrace({ administrateur }: { administrateur: boolean }) {
  const client = useQueryClient()
  const delais = useQuery(requetes.delaisGrace)
  if (!delais.data) return null
  const d = delais.data
  return (
    <Section titre="Délais de grâce">
      <p className="discret">
        « En retard » ne s’allume qu’une fois la date prévue et ce délai dépassés, et seulement si
        le relevé du compte couvre la période ; sinon « relevé manquant ». Les jours ouvrés suivent
        le calendrier bancaire.
      </p>
      <Ligne
        edition={
          administrateur
            ? (fermer) => (
                <Formulaire
                  champs={MOYENS_PAIEMENT.flatMap((m): Champ[] => [
                    {
                      nom: `${m}_jours`,
                      libelle: `${LIBELLES_MOYEN[m]} : jours`,
                      type: 'entier',
                      requis: true,
                    },
                    { nom: `${m}_ouvres`, libelle: 'jours ouvrés', type: 'booleen' },
                  ])}
                  initial={Object.fromEntries(
                    MOYENS_PAIEMENT.flatMap((m) => [
                      [`${m}_jours`, d[m].jours],
                      [`${m}_ouvres`, d[m].ouvres],
                    ]),
                  )}
                  onAnnuler={fermer}
                  onValider={async (v) => {
                    const valeurs = Object.fromEntries(
                      MOYENS_PAIEMENT.map((m) => [
                        m,
                        { jours: v[`${m}_jours`], ouvres: v[`${m}_ouvres`] },
                      ]),
                    )
                    await lire(
                      api.reglages['delais-grace'].$put({ json: corps(schP.delaisGrace, valeurs) }),
                    )
                    await client.invalidateQueries()
                    fermer()
                  }}
                />
              )
            : undefined
        }
      >
        <dl className="details">
          {MOYENS_PAIEMENT.map((m) => (
            <div key={m} className="contents">
              <dt>{LIBELLES_MOYEN[m]}</dt>
              <dd>
                {d[m].jours} jours{d[m].ouvres ? ' ouvrés' : ''}
              </dd>
            </div>
          ))}
        </dl>
      </Ligne>
    </Section>
  )
}

function LienAgenda() {
  const client = useQueryClient()
  const etat = useQuery(requetes.lienIcal)
  const [url, setUrl] = useState<string | null>(null)
  return (
    <Section titre="Mon agenda">
      <p className="discret">
        Un lien secret, à ajouter dans Google Agenda (Autres agendas, puis « À partir de l’URL »).
        Il ne montre que les échéances auxquelles vous avez accès. En créer un nouveau révoque
        l’ancien.
      </p>
      {url && (
        <div className="section-ajout">
          <p>Copiez ce lien maintenant : il ne sera plus affiché.</p>
          <input className="lien" readOnly value={url} onFocus={(e) => e.target.select()} />
          <button
            type="button"
            className="bouton-discret"
            onClick={() => navigator.clipboard?.writeText(url)}
          >
            Copier
          </button>
        </div>
      )}
      <div className="boutons">
        <button
          type="button"
          className="bouton"
          onClick={async () => {
            const r = await lire(api.auth.ical.$post())
            setUrl(r.url)
            await client.invalidateQueries({ queryKey: ['auth', 'ical'] })
          }}
        >
          {etat.data?.actif ? 'Remplacer mon lien' : 'Créer mon lien'}
        </button>
        {etat.data?.actif && (
          <button
            type="button"
            className="bouton-discret"
            onClick={async () => {
              await lire(api.auth.ical.$delete())
              setUrl(null)
              await client.invalidateQueries({ queryKey: ['auth', 'ical'] })
            }}
          >
            Révoquer
          </button>
        )}
      </div>
    </Section>
  )
}

// Résultat du retour de Google, avec l'adresse tentée quand elle est connue.
const RESULTATS_BOITE: Record<string, (adresse: string | undefined) => string> = {
  connectee: () =>
    'Boîte connectée. La première relève (trente derniers jours) a lieu dans le quart d’heure.',
  annulee: () => 'Connexion annulée : rien n’a été gardé.',
  echec: () => 'La connexion a échoué : recommencez.',
  adresse: () => 'Le compte choisi chez Google n’est pas l’adresse saisie : rien n’a été gardé.',
  portee: (a) =>
    `${a ?? 'La boîte'} n’a pas été connectée : Google n’a pas transmis l’autorisation de lire les e-mails, rien n’a été gardé. Recommencez et, sur l’écran des autorisations de Google, cochez la case de lecture des e-mails : elle n’est pas cochée d’office.`,
  hors_ligne: () => 'Google n’a pas donné d’accès durable : recommencez.',
  refusee: (a) =>
    `${a ?? 'Cette boîte'} ne peut pas être connectée ici : elle est déjà relevée pour une autre personne de l’appli, qui doit d’abord la déconnecter.`,
  indisponible: () => 'La connexion des boîtes n’est pas configurée.',
}

const ETATS_BOITE: Record<string, string> = {
  active: 'Relevée',
  reconnexion: 'À reconnecter',
  deconnectee: 'Déconnectée',
}

function connecterBoite(adresse: string, entiteId: string) {
  const q = new URLSearchParams({ adresse: adresse.trim(), ...(entiteId && { entiteId }) })
  window.location.assign(`/api/boites/google?${q}`)
}

/** Boîtes mail : les siennes, et celles qu'on voit par leur entité (toutes, pour un administrateur). */
function Boites({
  moi,
  resultat,
  adresseTentee,
}: {
  moi: { administrateur: boolean; ecriture: readonly string[] }
  resultat: string | undefined
  adresseTentee: string | undefined
}) {
  const client = useQueryClient()
  const boites = useQuery(requetes.boites)
  const entites = useQuery(requetes.entites)
  const ecrivables = (entites.data ?? []).filter((e) => !e.archivee && ecrit(moi, e.id))
  // Une connexion refusée repropose l'adresse tentée, pour recommencer d'un clic.
  const [adresse, setAdresse] = useState(
    resultat && resultat !== 'connectee' ? (adresseTentee ?? '') : '',
  )
  const [entiteId, setEntiteId] = useState('')
  const [message, setMessage] = useState<string | null>(
    () => (resultat && RESULTATS_BOITE[resultat]?.(adresseTentee)) || null,
  )
  const choixEntite = (valeur: string, changer: (v: string) => void, id: string) => (
    <select id={id} value={valeur} onChange={(e) => changer(e.target.value)}>
      <option value="">Aucune</option>
      {ecrivables.map((e) => (
        <option key={e.id} value={e.id}>
          {e.nom}
        </option>
      ))}
    </select>
  )
  return (
    <Section titre="Boîtes mail">
      <p className="discret">
        L’appli lit les boîtes Gmail connectées pour y repérer les demandes administratives (impôts,
        URSSAF, CAF, Ameli, notaire, banque…) : elle en fait des tâches. D’un mail, elle ne lit
        d’abord que l’expéditeur, l’objet et un extrait ; le reste n’est lu que s’il est retenu.
        Rien n’est gardé des autres mails. Si on l’autorise à la connexion, elle écrit aussi dans
        les brouillons de la boîte les courriels validés dans la boîte d’envoi, quand on le lui
        demande ; elle n’envoie jamais rien.
        {!moi.administrateur &&
          ' Dans vos boîtes, seuls les mails liés à vos entités sont retenus.'}
      </p>
      <p className="discret">
        L’entité d’une boîte dit qui la voit ici : les administrateurs voient toutes les boîtes,
        sauf celles d’un espace privé dont ils ne sont pas titulaires ; les autres, les leurs et
        celles de leurs entités. Chaque mail est classé à part : une facture de Flow Design reçue
        sur une boîte du foyer va à Flow Design. Une boîte rattachée à un espace privé ne range que
        dans cet espace, sauf si sa propriétaire l’ouvre à toutes ses entités.
      </p>
      {message && <p className="alerte">{message}</p>}
      {boites.data?.length === 0 && <Vide>Aucune boîte connectée.</Vide>}
      {boites.data?.map((b) => (
        <div key={b.id} className="ligne">
          <div className="ligne-contenu">
            <div className="titre-logo">
              <Logo cle="gmail" nom={b.adresse} />
              <strong>{b.adresse}</strong>
            </div>
            <span className={`badge ${b.etat === 'active' ? '' : 'retard-en_retard'}`}>
              {ETATS_BOITE[b.etat]}
            </span>
            <div className="discret">
              {b.aMoi ? 'Votre boîte' : `Boîte de ${b.personne}`}
              {!b.entiteModifiable && ` · ${b.entite ?? 'sans entité'}`}
              {b.agenda
                ? ' · agenda autorisé : les rendez-vous validés y sont posés'
                : ' · agenda non autorisé'}
              {b.brouillons
                ? ' · brouillons autorisés : les courriels validés peuvent y être écrits'
                : ' · brouillons non autorisés'}
            </div>
            <div className="discret">
              {b.releveeLe
                ? `Dernière relève le ${formaterInstant(b.releveeLe)}`
                : 'Pas encore relevée'}
              {` · ${b.demandes30j} demande(s) et ${b.pieces30j} pièce(s) repérées sur 30 jours`}
            </div>
            {b.derniereErreur && <div className="discret">{b.derniereErreur}</div>}
            {b.entiteModifiable && (
              <label className="filtre" htmlFor={`entite-boite-${b.id}`}>
                Entité{' '}
                {choixEntite(
                  b.entiteId ?? '',
                  async (v) => {
                    await lire(
                      api.boites[':id'].entite.$post({
                        param: { id: b.id },
                        json: { entiteId: v || null },
                      }),
                    )
                    await client.invalidateQueries({ queryKey: ['boites'] })
                  },
                  `entite-boite-${b.id}`,
                )}
              </label>
            )}
            {b.aMoi && b.espacePrive && (
              <label className="filtre" htmlFor={`rangement-boite-${b.id}`}>
                <input
                  id={`rangement-boite-${b.id}`}
                  type="checkbox"
                  checked={b.rangerHorsEspace}
                  onChange={async (e) => {
                    await lire(
                      api.boites[':id'].rangement.$post({
                        param: { id: b.id },
                        json: { rangerHorsEspace: e.target.checked },
                      }),
                    )
                    await client.invalidateQueries({ queryKey: ['boites'] })
                  }}
                />{' '}
                Ranger aussi hors de l’espace privé : les mails de cette boîte peuvent aller dans
                toutes vos entités (sinon, dans cet espace seulement)
              </label>
            )}
          </div>
          {b.aMoi && (
            <div className="boutons">
              {b.etat === 'reconnexion' && (
                <button
                  type="button"
                  className="bouton"
                  onClick={() => connecterBoite(b.adresse, '')}
                >
                  Reconnecter
                </button>
              )}
              {b.etat === 'active' && (!b.agenda || !b.brouillons) && (
                <button
                  type="button"
                  className="bouton"
                  onClick={() => connecterBoite(b.adresse, '')}
                >
                  {!b.agenda && !b.brouillons
                    ? 'Autoriser l’agenda et les brouillons'
                    : !b.agenda
                      ? 'Autoriser l’agenda'
                      : 'Autoriser les brouillons'}
                </button>
              )}
              <button
                type="button"
                className="bouton-discret"
                onClick={async () => {
                  if (
                    !(await confirmer(
                      `Déconnecter ${b.adresse} ? L’accès est retiré chez Google ; les tâches déjà créées sont gardées.`,
                    ))
                  )
                    return
                  const r = await lire(api.boites[':id'].deconnecter.$post({ param: { id: b.id } }))
                  setMessage(
                    r.revoqueChezGoogle
                      ? `${b.adresse} est déconnectée.`
                      : `${b.adresse} est déconnectée ici, mais Google n’a pas pu être joint : retirez aussi l’accès depuis myaccount.google.com/permissions.`,
                  )
                  await client.invalidateQueries({ queryKey: ['boites'] })
                }}
              >
                Déconnecter
              </button>
            </div>
          )}
        </div>
      ))}
      <form
        className="section-ajout"
        onSubmit={(e) => {
          e.preventDefault()
          if (adresse.trim()) connecterBoite(adresse, entiteId)
        }}
      >
        <label htmlFor="adresse-boite">Adresse Gmail (gmail.com ou Google Workspace)</label>
        <input
          id="adresse-boite"
          type="email"
          required
          autoComplete="email"
          value={adresse}
          onChange={(e) => setAdresse(e.target.value)}
        />
        <label htmlFor="entite-nouvelle-boite">Entité de la boîte</label>
        {choixEntite(entiteId, setEntiteId, 'entite-nouvelle-boite')}
        <div className="boutons">
          <button type="submit" className="bouton">
            Connecter une boîte Gmail
          </button>
        </div>
        <p className="discret">
          Une boîte à la fois. Google vous demandera de choisir ce compte et d’autoriser la lecture
          de Gmail : sur l’écran des autorisations, cochez la case de lecture des e-mails, elle
          n’est pas cochée d’office. Cochez aussi l’agenda (facultatif) : les rendez-vous que vous
          validez dans la boîte d’envoi seront posés dans votre calendrier principal, et nulle part
          ailleurs. L’appli n’étant pas vérifiée par Google, un avertissement s’affiche la première
          fois : « Paramètres avancés », puis « Accéder à admin PMVB ».
        </p>
      </form>
    </Section>
  )
}

/**
 * Supervision : administrateurs dans leur périmètre (les boîtes d'un espace privé dont ils ne
 * sont pas titulaires comptées par état, sans adresse) ; une titulaire d'espace privé voit la
 * même page, filtrée par l'API à son espace (pièces, boîtes, alertes). Jobs, sauvegarde et
 * taille de base sont de l'infrastructure partagée, réservés aux administrateurs : rien d'un
 * espace privé ne s'y lit, mais ce n'est pas son affaire, et l'API elle-même ne les porte pas
 * pour une titulaire (`d.administrateur`), pas seulement l'interface.
 */
function Supervision() {
  const r = useQuery(requetes.supervision)
  const acheminement = useQuery(requetes.acheminement).data
  if (!r.data) return null
  const d = r.data
  const suivi = Object.entries(acheminement ?? {}).filter(([, n]) => (n ?? 0) > 0)
  return (
    <Section titre="Supervision">
      <p>
        <Link to="/circuits" className="lien-fleche">
          Circuits et automatismes : l’audit du jour
          <Icone nom="arrow_forward" />
        </Link>
      </p>
      {d.alertes.length > 0 && (
        <div className="alerte">
          Alertes ouvertes :
          <ul>
            {d.alertes.map((a) => (
              <li key={a.id}>
                <Link to="/taches/$id" params={{ id: a.id }}>
                  {a.titre}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      <dl className="details">
        {d.administrateur && (
          <>
            <dt>Sauvegarde</dt>
            <dd>
              {d.sauvegarde
                ? `${d.sauvegarde.fraiche ? 'À jour' : 'En retard'} : il y a ${d.sauvegarde.ageHeures} h (${d.sauvegarde.fichier})`
                : 'Aucune sauvegarde enregistrée'}
            </dd>
            <dt>Base</dt>
            <dd>{d.base.taille}</dd>
          </>
        )}
        <dt>Pièces à vérifier</dt>
        <dd>{d.piecesAVerifier}</dd>
        <dt>Acheminement (Drive, cabinet)</dt>
        <dd>
          {suivi.length === 0 ? (
            'Tout est rangé et envoyé'
          ) : (
            <Link to="/pieces">
              {suivi
                .map(
                  ([etat, n]) =>
                    `${n} ${LIBELLES_ETAT_ACHEMINEMENT[etat as keyof typeof LIBELLES_ETAT_ACHEMINEMENT].toLowerCase()}`,
                )
                .join(', ')}
            </Link>
          )}
        </dd>
        <dt>Boîtes personnelles</dt>
        <dd>
          {d.boites.length === 0
            ? 'Aucune'
            : d.boites.map((b) => (
                <div key={b.id}>
                  {b.adresse} ({b.personne}) : {ETATS_BOITE[b.etat]}
                  {b.releveeLe
                    ? `, relevée le ${formaterInstant(b.releveeLe)}`
                    : ', jamais relevée'}
                  {b.derniereErreur && <span className="discret"> · {b.derniereErreur}</span>}
                </div>
              ))}
          {d.administrateur && d.boitesEspacesPrives.nombre > 0 && (
            <div className="discret">
              Espaces privés dont vous n’êtes pas titulaire : {d.boitesEspacesPrives.nombre}{' '}
              boîte(s), {d.boitesEspacesPrives.actives} active(s),{' '}
              {d.boitesEspacesPrives.aReconnecter} à reconnecter (sans adresse)
            </div>
          )}
        </dd>
        {d.administrateur && d.boitesEspacesPrives.nombre > 0 && (
          <>
            <dt>Boîtes des espaces privés</dt>
            <dd>
              {d.boitesEspacesPrives.nombre} boîte(s) : {d.boitesEspacesPrives.actives} active(s),{' '}
              {d.boitesEspacesPrives.aReconnecter} à reconnecter
              <div className="discret">Sans adresse : seule leur propriétaire les voit.</div>
            </dd>
          </>
        )}
      </dl>
      {d.administrateur && (
        <>
          {d.jobs.length === 0 && <Vide>Aucun job encore exécuté.</Vide>}
          {d.jobs.map((j) => (
            <div key={j.nom} className="ligne">
              <div className="ligne-contenu">
                <strong>{j.nom}</strong>{' '}
                {j.echecsConsecutifs > 0 && (
                  <span className="badge retard-en_retard">{j.echecsConsecutifs} échec(s)</span>
                )}
                <div className="discret">
                  {j.derniereReussite
                    ? `Réussi le ${formaterInstant(j.derniereReussite)}`
                    : 'Jamais réussi'}
                  {j.echecsConsecutifs > 0 && j.derniereErreur && ` · ${j.derniereErreur}`}
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </Section>
  )
}

function PiecesEtIa() {
  const r = useQuery(requetes.reglagesPieces)
  if (!r.data) return null
  const d = r.data
  const dollars = (micro: number) => `${(micro / 1_000_000).toFixed(2).replace('.', ',')} $`
  return (
    <Section titre="Pièces et IA">
      <dl className="details">
        <dt>Dépense IA ({d.depense.mois})</dt>
        <dd>
          {dollars(d.depense.coutMicroUsd)} sur un plafond de {dollars(d.plafondMicroUsd)} ·{' '}
          {d.depense.appels} appel(s)
          <div className="discret">
            Jetons : {d.depense.jetons.entree} en entrée, {d.depense.jetons.sortie} en sortie,{' '}
            {d.depense.jetons.cacheLecture} lus en cache.
          </div>
        </dd>
        <dt>Boîte relevée</dt>
        <dd>
          {d.gmail.adresse ?? 'Aucune (gmail.adresse et ses accès absents de la configuration)'}
          <div className="discret">
            {d.gmail.synchroniseLe
              ? `Dernière relève le ${formaterInstant(d.gmail.synchroniseLe)}`
              : 'Jamais relevée'}
            {d.gmail.expiration && `, surveillance jusqu’au ${formaterInstant(d.gmail.expiration)}`}
          </div>
        </dd>
      </dl>
      {d.absents.length > 0 && (
        <div className="alerte">
          Pas encore configuré (docs/actions-humaines.md) :
          <ul>
            {d.absents.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
      )}
    </Section>
  )
}

function ApercuDigest() {
  const apercu = useQuery(requetes.apercuDigest)
  return (
    <Section titre="Mon digest du matin">
      <p className="discret">
        Chaque jour ouvré à 7 h 30, en message privé Slack, si « Slack » est choisi comme canal de
        notification dans votre fiche (Personnes) avec votre identifiant Slack. Voici ce qu’il
        dirait aujourd’hui.
      </p>
      {apercu.isPending && <Vide>Chargement…</Vide>}
      {apercu.data && <pre className="apercu-digest">{apercu.data.texte}</pre>}
    </Section>
  )
}

export function Reglages({
  resultatBoite,
  adresseBoite,
  resultatBanque,
  onglet,
  onChangeOnglet,
  connexion,
  onChangeConnexion,
}: {
  resultatBoite?: string | undefined
  adresseBoite?: string | undefined
  resultatBanque?: string | undefined
  onglet: OngletReglages | undefined
  onChangeOnglet: (o: OngletReglages) => void
  connexion: OngletConnexions | undefined
  onChangeConnexion: (c: OngletConnexions) => void
}) {
  const moi = useSuspenseQuery(requeteMoi).data
  // Onglet « Système » (Supervision) : administrateurs, et titulaire d'un espace privé, qui
  // n'y voit que son espace.
  const voitSysteme = moi.administrateur || moi.espacesPrives.length > 0
  // Sans onglet choisi, le retour de Google ou de la banque ouvre « Connexions ».
  const demande = onglet ?? (resultatBoite || resultatBanque ? 'connexions' : 'echeances')
  const vue: OngletReglages = demande === 'systeme' && !voitSysteme ? 'echeances' : demande
  // « Banques » : administrateurs seulement ; le retour de la banque ouvre ce sous-onglet.
  const demandeConnexion = connexion ?? (resultatBanque ? 'banques' : 'boites')
  const vueConnexion: OngletConnexions =
    demandeConnexion === 'banques' && !moi.administrateur ? 'boites' : demandeConnexion
  const client = useQueryClient()
  const regles = useQuery(requetes.regles)
  const entites = useQuery(requetes.entites)
  const tiers = useQuery(requetes.tiers)
  const [bilan, setBilan] = useState<{
    creees: number
    deplacees: number
    retirees: number
  } | null>(null)
  const gerees = (entites.data ?? []).filter((e) => !e.archivee && gere(moi, e.id))
  const parEntite = new Map<string, Regle[]>()
  for (const r of regles.data ?? [])
    parEntite.set(r.entite, [...(parEntite.get(r.entite) ?? []), r])
  const modifiable = (r: Regle) => gerees.some((e) => e.id === r.entiteId)
  const baux = useQuery(requetes.tousLesBaux).data ?? []
  const nomTiers = (id: string | null) => tiers.data?.find((t) => t.id === id)?.nom
  const optionsBaux = baux.map((b) => ({
    valeur: b.id,
    entiteId: b.entiteId,
    libelle: `${(entites.data ?? []).find((e) => e.id === b.entiteId)?.nom ?? ''} · bail de ${nomTiers(b.locataireId) ?? 'locataire inconnu'}${b.gestionnaireId ? `, géré par ${nomTiers(b.gestionnaireId)}` : ''}`,
  }))

  return (
    <main>
      <EntetePage surtitre="Administration" titre="Réglages" />
      <Onglets
        valeur={vue}
        onChange={onChangeOnglet}
        defilants
        onglets={[
          { valeur: 'echeances', libelle: 'Échéances', icone: 'event_upcoming' },
          { valeur: 'tri', libelle: 'Consignes de tri', icone: 'filter_list' },
          { valeur: 'connexions', libelle: 'Connexions', icone: 'mail' },
          { valeur: 'moi', libelle: 'Agenda, digest, notifications', icone: 'person' },
          ...(voitSysteme
            ? [{ valeur: 'systeme' as const, libelle: 'Système', icone: 'shield' as const }]
            : []),
        ]}
      />
      {vue === 'echeances' && (
        <>
          <Section
            titre="Règles d’échéances"
            libelleAjout="Nouvelle règle"
            ajout={
              gerees.length > 0
                ? (fermer) => (
                    <Formulaire
                      champs={champsRegle({ entites: gerees }, tiers.data ?? [], optionsBaux)}
                      initial={{
                        depuis: '2026-01-01',
                        periodeUnite: 'mois',
                        periodeDecalage: 0,
                        certitude: 'a_confirmer',
                        active: true,
                      }}
                      onAnnuler={fermer}
                      onValider={async (v) => {
                        const r = await lire(
                          api.regles.$post({ json: corps(sch.regleCreation, depuisFormulaire(v)) }),
                        )
                        setBilan(r.bilan)
                        await client.invalidateQueries()
                        fermer()
                      }}
                    />
                  )
                : undefined
            }
          >
            <p className="discret">
              Chaque règle crée les échéances des douze prochains mois ; modifier une règle déplace
              ses échéances à venir, sans toucher aux dates saisies à la main ni aux échéances déjà
              traitées.
            </p>
            <Bilan bilan={bilan} />
            {regles.isPending && <Vide>Chargement…</Vide>}
            {[...parEntite.entries()].map(([entite, liste]) => (
              <div key={entite} className="bloc">
                <h3>{entite}</h3>
                {liste.map((r) => (
                  <Ligne
                    key={r.id}
                    edition={
                      modifiable(r)
                        ? (fermer) => (
                            <Formulaire
                              champs={champsRegle(
                                null,
                                tiers.data ?? [],
                                optionsBaux.filter((b) => b.entiteId === r.entiteId),
                              )}
                              initial={versFormulaire(r)}
                              onAnnuler={fermer}
                              onValider={async (v) => {
                                const res = await lire(
                                  api.regles[':id'].$patch({
                                    param: { id: r.id },
                                    json: corps(sch.regleModification, depuisFormulaire(v, r)),
                                  }),
                                )
                                setBilan(res.bilan)
                                await client.invalidateQueries()
                                fermer()
                              }}
                            />
                          )
                        : undefined
                    }
                  >
                    <strong>{r.libelle}</strong>
                    {!r.active && <span className="badge">Inactive</span>}
                    {r.verification && <span className="badge">Vérification</span>}
                    {r.certitude === 'a_confirmer' && <span className="badge">À confirmer</span>}
                    <div className="discret">
                      {[
                        decrire(r.rrule),
                        r.decalage.fenetreJours
                          ? `fenêtre de ${r.decalage.fenetreJours} jours`
                          : null,
                        r.decalage.reportJourOuvre ? 'reportée au jour ouvré' : null,
                        r.montantPrevuCts !== null
                          ? formaterMontant(r.montantPrevuCts, r.devise)
                          : null,
                        r.paiement
                          ? `${LIBELLES_SENS[r.paiement.sens].toLowerCase()} par ${LIBELLES_MOYEN[r.paiement.moyen].toLowerCase()}`
                          : null,
                        r.verification
                          ? decrireVerification(
                              r.verification,
                              nomTiers(r.verification.tiersId ?? null) ?? null,
                            )
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                    {r.note && <div className="discret">{r.note}</div>}
                    {r.taches.length > 0 && (
                      <div className="discret">
                        Tâches :{' '}
                        {r.taches
                          .map(
                            (m) =>
                              `${m.titre} (${m.decalageJoursOuvres === 0 ? 'à la date limite' : `${m.decalageJoursOuvres} j ouvrés`}${m.executant === 'cabinet' ? ', cabinet' : m.executant === 'gestionnaire' ? ', gestionnaire du bail' : ''})`,
                          )
                          .join(' ; ')}
                      </div>
                    )}
                  </Ligne>
                ))}
              </div>
            ))}
          </Section>
          <DelaisGrace administrateur={moi.administrateur} />
        </>
      )}
      {vue === 'tri' && <SectionConsignes />}
      {vue === 'connexions' && (
        <>
          <Onglets
            valeur={vueConnexion}
            onChange={onChangeConnexion}
            soulignes
            onglets={[
              { valeur: 'boites', libelle: 'Boîtes mail' },
              ...(moi.administrateur ? [{ valeur: 'banques' as const, libelle: 'Banques' }] : []),
              { valeur: 'portails', libelle: 'Portails' },
              { valeur: 'fournisseurs', libelle: 'Fournisseurs' },
              { valeur: 'ordinateurs', libelle: 'Ordinateurs' },
            ]}
          />
          {vueConnexion === 'boites' && (
            <Boites moi={moi} resultat={resultatBoite} adresseTentee={adresseBoite} />
          )}
          {vueConnexion === 'banques' && <Banques resultat={resultatBanque} />}
          {vueConnexion === 'portails' && <Portails administrateur={moi.administrateur} />}
          {vueConnexion === 'fournisseurs' && <Fournisseurs />}
          {vueConnexion === 'ordinateurs' && <Ordinateurs />}
        </>
      )}
      {vue === 'moi' && (
        <>
          <SectionNotifications />
          <LienAgenda />
          <ApercuDigest />
        </>
      )}
      {vue === 'systeme' && (
        <>
          <Supervision />
          {moi.administrateur && <PiecesEtIa />}
          {moi.espacesPrives.length > 0 && <DepenseIaDeMesEspaces />}
        </>
      )}
    </main>
  )
}
